import React, { Component, ChangeEvent } from 'react';
import { withTranslation } from 'react-i18next';
import { createSearchParams, NavigateFunction } from 'react-router';
import { TFunction } from 'i18next';
import FormMultiselect from '../Form/components/FormMultiselect/FormMultiselect';
import { FilterEventType } from './partials/FilterEventType';
import { Namespace } from '../../../translation/namespace';
import { DateFilter, FilterDate } from './partials/FilterDate';
import { SortBy } from './partials/SortBy';
import Btn from '../Btn';
import { TooltipPlacement } from '../../atoms/Tooltip';
import { Icons } from '../../atoms/SVG';
import withAppContext from '../../hocs/withAppContext/withAppContext';
import { FACETS } from './ResultListSettings.enums';
import { withRouter } from '../../hocs/withRouter';
import { SectionTypes } from '../../../types/api/Sections';
import { FacetLink } from './partials/FacetLink';
import { FilterPerson } from './partials/FilterPerson';
import { FilterLink, SelectLinks } from '../../../_lib/components/SelectLinks/SelectLinks';
import { translatePageType } from '../../../_lib/components/PageType/PageTypeTitle';
import { PageType } from '../../../types/api/PageType';

type Facet = {
  suggest?: { url: string };
  type: 'multiSelect' | 'singleSelect' | 'personSuggest';
  items: FacetItem[];
};

type FacetItem = {
  name: string;
  label: string;
  active: boolean;
  resultNumber: number;
  startDate: DateFilter;
  endDate: DateFilter;
  url: string;
};

type ResultListSettingsProps = {
  sort?: FacetLink[];
  searchStrictness?: FacetLink[];
  filter?: FacetLink[];
  facets?: Record<FACETS, Facet> | null;
  portalConfiguration?: {
    son_page_confidentialLevel_highest: string;
  };
  resetAllFiltersUrl?: {
    url: string;
  };

  navigate: NavigateFunction;
  location: Location;
  // Translation
  t: TFunction;
  wantedFacets?: FACETS[];
  customFacetTranslations?: Record<string, { title: string; itemTexts: Record<string, string> }>;
  customSortOptionTranslations?: Record<string, string>;
};

export type StateFacetItem = {
  text: string;
  value: string;
  active: boolean;
  url: string;
  name: FACETS;
  additionalInformation: number;
  invisibleLabelInformation: string;
  readOnly?: boolean;
  assignable?: boolean;
};
type StateFacet = { title: string; name: FACETS; type: string; items: StateFacetItem[] };

type ResultListSettingsState = {
  facets: Record<FACETS, StateFacet> | null;
  facetsHaveChanged: boolean;
};

/**
 * A set of Filters & Sorting options for manipulating search results
 *
 * Facets handled with FormMultiSelect must be kept in state, as we do not have the FormModel magic here.
 *  -> need to keep track of activated Items.
 *  -> manually reset facets if they have changed.
 */
class ResultListSettings extends Component<ResultListSettingsProps, ResultListSettingsState> {
  private readonly query: { [p: string]: string };

  constructor(props: ResultListSettingsProps) {
    super(props);
    this.state = {
      facets: this.structureFormMultiSelectFacets(),
      facetsHaveChanged: false,
    };

    this.query = Object.fromEntries(createSearchParams(props.location.search));
  }

  componentDidUpdate(prevProps: ResultListSettingsProps) {
    if (
      this.props.customFacetTranslations !== prevProps.customFacetTranslations ||
      this.props.facets !== prevProps.facets
    ) {
      this.setState({
        facets: this.structureFormMultiSelectFacets(),
        facetsHaveChanged: false,
      });
    }
  }

  /**
   * Event handler for submit event
   */
  onMultiselectSubmit = (event: SubmitEvent, activeItems: any[], key: string) => {
    event.preventDefault();
    const facetParams = activeItems.reduce((a, i) => a.concat(i.value), []);

    let queryObject;
    if (activeItems.length > 0) {
      queryObject = {
        ...this.query,
        [key]: facetParams,
      };
    } else {
      queryObject = { ...this.query };
      delete queryObject[key];
    }
    this.onFacetEvent(queryObject);
  };

  /**
   * Event handler for onChange event on multi select
   * @param event
   */
  onMultiselectChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;
    const facetToChange = this.state.facets ? this.state.facets[name as FACETS] : null;

    if (facetToChange) {
      const itemToChange = facetToChange.items.find(item => item.value === value);

      if (itemToChange) {
        itemToChange.active = checked;
        this.setState({ facetsHaveChanged: true });
      }
    }
  };

  resetFacetChanges = (facetKey: FACETS) => {
    if (this.state.facetsHaveChanged) {
      // @ts-expect-error code is working and typing problem not really clear.
      // will change when refactoring to functional component
      this.setState(prevState => {
        return {
          facetsHaveChanged: false,
          ...(prevState.facets ? prevState.facets[facetKey] : {}),
          ...this.structureFacets(facetKey),
        };
      });
    }
  };

  /**
   * Setting the facets we get from JSON into correct form for our MultiSelect.
   * The key in JSON will also be the param that is used in URL.
   *
   * fq_contentDate does not need to be structured
   */
  structureFormMultiSelectFacets = () => {
    const formMultiSelectFacets: FACETS[] = this.props.wantedFacets || [];

    // if any maxConfidentialLevel is set in settings, we will show this facet
    if (this.props.portalConfiguration?.son_page_confidentialLevel_highest) {
      formMultiSelectFacets.push(FACETS.FQ_CLEARANCE_LEVEL);
    }

    if (this.props.facets) {
      return Object.keys(this.props.facets).reduce((acc, cur) => {
        return {
          ...acc,
          ...(formMultiSelectFacets.includes(cur as FACETS) ? { [cur]: this.structureFacets(cur as FACETS) } : {}),
        };
      }, {}) as Record<FACETS, StateFacet>;
    }
    return null;
  };

  /**
   * Structures given Array of facets from props for FormMultiSelect.
   */
  structureFacets = (facetKey: FACETS) => {
    const facet = this.props.facets?.[facetKey];
    if (!facet) {
      return {};
    }
    const invisibleLabelInformation = (number: number) => {
      return number > 1 ? this.props.t('countNumber_plural') : this.props.t('countNumber');
    };

    return {
      title: this.props.customFacetTranslations?.[facetKey]?.title || this.props.t(`facets.${facetKey}.title`),
      name: facetKey,
      type: facet.type,
      suggest: facet.suggest,
      items: facet.items?.map(item => ({
        text:
          this.props.customFacetTranslations?.[facetKey]?.itemTexts?.[item.label] ||
          this.translateLabelTextAccordingToFacetKey(item.label, facetKey),
        value: item.label,
        active: item.active,
        name: item.name,
        url: item.url,
        additionalInformation: item.resultNumber,
        invisibleLabelInformation: invisibleLabelInformation(item.resultNumber),
      })),
    };
  };

  renderAllFacets = () => {
    if (this.state.facets) {
      return Object.keys(this.state.facets).map(facet => this.renderFacet(facet as FACETS));
    }

    return [];
  };

  /**
   * Certain Labels are from Backend and cant be translated, e.g. the "documentTypes".
   */
  translateLabelTextAccordingToFacetKey = (labelText: string, facetKey: FACETS) => {
    switch (facetKey) {
      case FACETS.FQ_ARCHIVEDATE:
      case FACETS.FQ_VALIDDATE:
      case FACETS.FQ_PACKSTATE:
        return this.props.t(`facets.${facetKey}.${labelText}`);
      case FACETS.FQ_CLEARANCE_LEVEL:
        return this.props.t(`${Namespace.phrases}:securityClearance.${labelText}.text`);
      case FACETS.FQ_PROFILE_FIELDS:
        return this.props.t(`${Namespace.UserSearchResults}:fieldNames.${labelText}`);
      case FACETS.FQ_PAGETYPE:
        return translatePageType(labelText as PageType);
      default:
        return labelText;
    }
  };

  /**
   * Return a boolean whether we have active items or not.
   *
   * Used to i.e. disable the submit button for FACETS.FQ_ARCHIVEDATE
   * ref: SON-8150
   */
  hasActiveItems = (facet: StateFacet) => facet.items.filter(i => i.active).length > 0;

  /**
   * Renders a single Facet
   * @param facetParamName
   * @returns {*}
   */
  renderFacet = (facetParamName: FACETS): any => {
    const facet = this.state.facets ? this.state.facets[facetParamName] : null;
    switch (facet?.type) {
      case 'personSingleSelect':
        return this.renderPersonFilter(facetParamName, 'id');
      case 'personSuggest':
        return this.renderPersonFilter(facetParamName, 'profileNameWithoutHighlighting');
      case 'multiSelect':
        return this.renderMultiSelectFacet(facetParamName, facet);
      case 'singleSelect':
        return this.renderSingleSelectFacet(facetParamName, facet);
      default:
        return null;
    }
  };

  private renderMultiSelectFacet(key: FACETS, facet: StateFacet | null) {
    return facet ? (
      <div key={`ResultListFacet-${key}`} className="result-list-settings__box">
        <FormMultiselect
          key={`ResultListFacet-multiselect-${key}`}
          buttonHeadline={this.props.t(`filterButton.headline`)}
          hierarchy={3}
          title={facet.title}
          name={facet.name}
          items={facet.items}
          showButtons
          disableSubmitButton={facet.name === FACETS.FQ_ARCHIVEDATE && !this.hasActiveItems(facet)}
          // Events
          onSubmit={this.onMultiselectSubmit}
          onChange={this.onMultiselectChange}
          onCloseResetFacet={() => this.resetFacetChanges(key)}
          sortAphabetically={facet.name === FACETS.FQ_PROFILE_FIELDS}
        />
      </div>
    ) : null;
  }

  onFacetEvent = (queryObject: Record<string, string>) => {
    // changing filter means resetting pagination
    // while we are moving away from ON2, both "page" and "pageNumber" are valid params
    const queryObjectWithoutPage = { ...queryObject };
    delete queryObjectWithoutPage.page;
    delete queryObjectWithoutPage.pageNumber;
    // forcing refetch, so a search with the same term can be sent out multiple times
    const encodedQuery = new URLSearchParams(queryObjectWithoutPage).toString();

    this.props.navigate(`?${encodedQuery}`, {
      state: {
        refetch: 'force',
        omitSection: [SectionTypes.personal],
      },
    });
  };

  renderFilterDate = () => {
    if (
      this.props.facets &&
      this.props.facets[FACETS.FQ_CONTENTDATE] &&
      this.props.facets[FACETS.FQ_CONTENTDATE].items[0].label === 'userPeriod'
    ) {
      const { label, startDate, endDate, active } = this.props.facets[FACETS.FQ_CONTENTDATE].items[0];
      return (
        <div className="result-list-settings__box">
          <FilterDate
            facetKey={FACETS.FQ_CONTENTDATE}
            facetType={label}
            active={active}
            startDate={startDate}
            endDate={endDate}
            onSubmit={this.onFacetEvent}
            query={this.query}
          />
        </div>
      );
    }
  };

  renderFilterEventType = () => {
    if (this.props.filter && this.props.filter.length > 0) {
      return (
        <div className="result-list-settings__box">
          <FilterEventType eventStartDate={this.props.filter} />
        </div>
      );
    }
    return null;
  };

  renderResetAllFiltersButton = () => {
    return this.props.resetAllFiltersUrl ? (
      <div className="result-list-settings__box">
        <Btn
          href={this.props.resetAllFiltersUrl.url}
          hiddenText
          text={this.props.t('clearFilterButton')}
          titleText={this.props.t('clearFilterButton')}
          tooltipPlacement={TooltipPlacement.TOP}
          svgSpriteId={Icons.ERASER}
        />
      </div>
    ) : null;
  };

  renderSearchStrictness = () => {
    return this.props.searchStrictness ? (
      <div className="result-list-settings__box">
        <SortBy
          key={this.props.t('searchStrictness.headline')}
          sortLinks={this.props.searchStrictness}
          headline={this.props.t('searchStrictness.headline')}
          getOptionName={itemName => this.props.t(`searchStrictness.options.${itemName}`)}
        />
      </div>
    ) : null;
  };

  renderPersonFilter = (facetKey: FACETS, valueKey: string) => {
    const facet = this.props.facets?.[facetKey];

    if (facet) {
      const { label, active } = facet.items[0] || {
        label: '',
        active: false,
      };

      return (
        <div className="result-list-settings__box">
          <FilterPerson
            active={active}
            facetKey={facetKey}
            valueKey={valueKey}
            onSubmit={this.onFacetEvent}
            query={this.query}
            searchTerm={label}
            suggestUrl={facet.suggest?.url || '<missing url>'}
          />
        </div>
      );
    }
  };

  renderSingleSelectFacet = (facetName: string, facet: StateFacet) => {
    const selectLinks: FilterLink[] = facet.items.map(facetItem => ({
      name: `${this.props.t(`facets.${facetName}.${facetItem.value}`)} (${facetItem.additionalInformation})`,
      url: facetItem.url,
      active: facetItem.active,
    }));

    return (
      <div className="result-list-settings__box">
        <SelectLinks headline={this.props.t(`facets.${facetName}.title`)} links={selectLinks} />
      </div>
    );
  };

  renderSortBy = () => {
    return this.props.sort ? (
      <div className="result-list-settings__box result-list-settings__box--sort">
        <SortBy
          key={this.props.t('sort.headline')}
          sortLinks={this.props.sort}
          headline={this.props.t('sort.headline')}
          getOptionName={itemName =>
            this.props.customSortOptionTranslations?.[itemName] || this.props.t(`sort.options.${itemName}`)
          }
        />
      </div>
    ) : null;
  };

  render() {
    return (
      <div className="result-list-settings">
        <h2 className="result-list-settings__headline">{this.props.t('headline')}</h2>
        {this.renderSearchStrictness()}
        {this.renderFilterEventType()}
        {this.renderAllFacets()}
        {this.renderFilterDate()}
        {this.renderResetAllFiltersButton()}
        {this.renderSortBy()}
      </div>
    );
  }
}

export default withRouter(withTranslation(Namespace.ResultListSettings)(withAppContext(ResultListSettings)));
